import { z } from 'zod';
import {
  ageYearsSchema,
  idParamSchema,
  moneySchema,
  optionalEmail,
  optionalPhone,
  optionalString,
  paginationQuerySchema,
  percentageSchema,
  requiredString,
} from './common';
import { genderSchema, maritalStatusSchema, paymentMethodSchema } from './enums';

const patientFields = {
  name: requiredString('Name'),
  gender: genderSchema,
  age_year: ageYearsSchema,
  // Months and days are optional: most patients give an age in years only.
  age_month: z.coerce.number().int().min(0).max(11).default(0),
  // 31, not 30: a neonate registered on the 31st of a long month is a real age.
  age_days: z.coerce.number().int().min(0).max(31).default(0),
  refer_by: optionalString(),
  // Contact details are optional. Requiring them is right at the front desk and
  // impossible for the years of historical visits being imported from the old
  // spreadsheets — those sheets carry neither. Synthesising a placeholder
  // address is the alternative, and poisoned contact data eventually gets
  // mailed.
  phone_number: optionalPhone(),
  cnic_number: optionalString(),
  email: optionalEmail(),
  note: optionalString(),
  discount_rupees: moneySchema.default(0),
  discount_percentage: percentageSchema.default(0),
  tax_percentage: percentageSchema.default(0),
  other_charges: moneySchema.default(0),
  other_charges_label: optionalString(),
  /**
   * Money received with this save — not the running total.
   *
   * `paid` is never posted: storing it verbatim would let an edit overwrite it,
   * with no record of who took the money or when. Each save appends at most
   * one `Payment`, attributed to the session; `Patient.paid` is the ledger sum.
   */
  payment_amount: moneySchema.default(0),
  payment_method: paymentMethodSchema.nullish(),
  married_status: maritalStatusSchema,
  panel_ids: z.array(idParamSchema).default([]),
  /**
   * Permission to send this patient's reports on WhatsApp.
   *
   * Only the flag. `whatsapp_consent_at` and `whatsapp_consent_by_id` are
   * stamped from the session — a request that could set them could forge a
   * consent record, which is the one piece of evidence that a report was sent
   * with the patient's agreement.
   */
  whatsapp_consent: z.boolean().default(false),
};

/**
 * An extra charge has to say what it is for, and money received has to say how
 * it arrived.
 *
 * The charge label is also a CHECK constraint, and the method is written onto
 * the `Payment` this save appends. They live here too so
 * the front desk gets a message under the right input instead of a 500.
 *
 * The method is a hard rule rather than coerced away: it describes this one
 * payment, not a stored field that drifts independently of the amount on a
 * later edit.
 */
export function refineBilling(
  value: {
    other_charges: number;
    other_charges_label?: string | null | undefined;
    payment_amount: number;
    payment_method?: string | null | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  if (value.other_charges > 0 && !value.other_charges_label?.trim()) {
    ctx.addIssue({
      code: 'custom',
      path: ['other_charges_label'],
      message: 'Say what the charge is for',
    });
  }
  if (value.payment_amount > 0 && !value.payment_method) {
    ctx.addIssue({
      code: 'custom',
      path: ['payment_method'],
      message: 'Say how the money was received',
    });
  }
}

/**
 * The client sends only the lab tests it selected. The server looks up each
 * LabTest and copies its name and price onto the created Report.
 *
 * Report prices and `user_id` are server-controlled, never taken from the
 * body, so a caller cannot set an arbitrary price or act as another user. So
 * are the generated `pin_no` and `mr_no`.
 *
 * `total`, `balance`, `payment_status`, `discount_amount` and `tax_amount` are
 * absent for the same reason. Stored verbatim from the body, a crafted request
 * could book a Rs 5,000 panel for Rs 0 — and every revenue figure in the app
 * reads those columns. The server derives
 * all of them from `test_ids`, the discounts, the tax rate, the extra charges
 * and the payment ledger using `calculatePricing` in shared/pricing.ts, which is the same
 * function the form renders its summary from.
 */
export const createPatientSchema = z
  .object({
    ...patientFields,
    test_ids: z.array(idParamSchema).min(1, 'Select at least one lab test'),
  })
  .superRefine(refineBilling);
export type CreatePatientInput = z.infer<typeof createPatientSchema>;

/**
 * Validation is identical to create — the difference is entirely in what the
 * server *does* with it. On update it reconciles reports against `test_ids`,
 * adding newly selected tests and removing deselected ones while leaving
 * untouched tests alone, so an edit never discards entered results.
 *
 * `payment_amount` is appended to the ledger, never a replacement for what was
 * already paid. Correcting money already taken is a refund — see
 * `recordPaymentSchema`.
 */
export const updatePatientSchema = createPatientSchema;
export type UpdatePatientInput = z.infer<typeof updatePatientSchema>;

/**
 * The patient list, optionally narrowed to a date range.
 *
 * Added for the year-wise history screen, whose rows drill through to
 * `/patients?from=&to=` — a count on a report that cannot be opened is a dead
 * end. Inclusive calendar dates; the server turns them into a half-open instant
 * range with `dayRange` so the final day is not silently dropped.
 */
export const patientQuerySchema = paginationQuerySchema.extend({
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});
export type PatientQuery = z.infer<typeof patientQuerySchema>;

/**
 * A payment or refund recorded against a visit after registration — the balance
 * collected when the patient comes back for the report, or money handed back.
 *
 * Negative is a refund, and a refund has to say why: it is the row the drawer
 * count will be asked about. The migration enforces the same rule.
 */
export const recordPaymentSchema = z
  .object({
    amount: z.coerce
      .number()
      .multipleOf(0.01)
      .refine((value) => value !== 0, 'Enter an amount')
      .refine((value) => Math.abs(value) <= 99_999_999.99, 'Amount is too large'),
    method: paymentMethodSchema,
    note: optionalString(),
  })
  .superRefine((value, ctx) => {
    if (value.amount < 0 && !value.note?.trim()) {
      ctx.addIssue({ code: 'custom', path: ['note'], message: 'Say why the money was refunded' });
    }
  });
export type RecordPaymentInput = z.infer<typeof recordPaymentSchema>;

/** `GET /patients/lookup?phone=` — is this number somebody the lab has seen? */
export const patientLookupQuerySchema = z.object({
  phone: z.string().trim().min(1, 'Enter a phone number'),
});
export type PatientLookupQuery = z.infer<typeof patientLookupQuerySchema>;
